/**
 * ProviderReviews
 * Manage reviews: view, reply, report, average rating breakdown, and rating trend chart.
 */
import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from 'recharts';
import api from '../api';
import StatusBadge from '../components/StatusBadge';

const SOURCES = ['Google', 'SM-Compare', 'Yelp', 'Facebook', 'Trustpilot'];

function generateMockReviews() {
  const names = [
    'John M.', 'Marie L.', 'David K.', 'Sarah J.', 'Ahmed H.',
    'Jessica B.', 'Chris E.', 'Priya P.', 'Robert F.', 'Lisa W.',
    'Thomas Y.', 'Karen T.',
  ];
  const texts = [
    'Excellent service, very professional team. Would recommend to anyone in Ontario.',
    'Good value for money, but response time could be improved.',
    'Outstanding customer support. They went above and beyond.',
    'Decent service overall. The pricing is competitive compared to others in the sector.',
    'Had some issues initially but they resolved everything quickly.',
    'Very satisfied with the quality. Been a customer for 2 years now.',
    'Average experience. Nothing special but nothing bad either.',
    'Great onboarding process and the team is very knowledgeable.',
    'Would have liked more transparency in pricing.',
    'Top-notch service. The best provider we have worked with in Canada.',
    'Reliable and consistent. No complaints at all.',
    'Friendly staff but the website could use some improvement.',
  ];

  return names.map((name, i) => ({
    id: `review-${i + 1}`,
    author: name,
    source: SOURCES[i % SOURCES.length],
    rating: Math.max(1, Math.min(5, 3 + Math.floor(Math.random() * 3))),
    text: texts[i],
    date: new Date(Date.now() - i * 86400000 * 5).toISOString(),
    reply: i === 0 ? 'Thank you for your kind words, John! We appreciate your business.' : null,
    reported: false,
  }));
}

function generateRatingBreakdown() {
  return SOURCES.map((source) => ({
    source,
    avgRating: parseFloat((3.5 + Math.random() * 1.5).toFixed(1)),
    count: Math.floor(10 + Math.random() * 90),
  }));
}

function generateRatingTrend() {
  const months = ['Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb'];
  return months.map((month) => ({
    month,
    avgRating: parseFloat((3.8 + Math.random() * 0.8).toFixed(1)),
  }));
}

function StarRating({ rating }) {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <span key={i} style={{ color: i <= rating ? '#f59e0b' : '#d1d5db', fontSize: '16px' }}>
        &#9733;
      </span>
    );
  }
  return <span style={{ display: 'inline-flex', gap: '1px' }}>{stars}</span>;
}

export default function ProviderReviews() {
  const queryClient = useQueryClient();
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');

  // Fetch reviews
  const { data: reviewsRaw } = useQuery({
    queryKey: ['provider-reviews'],
    queryFn: () => api.get('/api/provider-portal/reviews').catch(() => null),
    placeholderData: null,
  });

  const reviews = reviewsRaw?.reviews ?? generateMockReviews();
  const ratingBreakdown = reviewsRaw?.breakdown ?? generateRatingBreakdown();
  const ratingTrend = reviewsRaw?.trend ?? generateRatingTrend();

  const overallAvg = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : '0.0';

  // Reply mutation
  const replyMutation = useMutation({
    mutationFn: ({ reviewId, reply }) =>
      api.post(`/api/provider-portal/reviews/${reviewId}/reply`, { reply }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provider-reviews'] });
      setReplyingTo(null);
      setReplyText('');
    },
  });

  // Report mutation
  const reportMutation = useMutation({
    mutationFn: (reviewId) =>
      api.post(`/api/provider-portal/reviews/${reviewId}/report`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['provider-reviews'] }),
  });

  const handleReplySubmit = useCallback((reviewId) => {
    if (!replyText.trim()) return;
    replyMutation.mutate({ reviewId, reply: replyText.trim() });
  }, [replyText, replyMutation]);

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b', marginBottom: '4px' }}>
          Reviews
        </h1>
        <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>
          View and respond to customer reviews across all platforms.
        </p>
      </div>

      {/* Overview stats */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <div style={{
          flex: '1 1 180px', padding: '20px', backgroundColor: '#fff', borderRadius: '12px',
          border: '1px solid #e2e8f0', textAlign: 'center',
        }}>
          <div style={{ fontSize: '36px', fontWeight: '700', color: '#1e293b' }}>{overallAvg}</div>
          <StarRating rating={Math.round(parseFloat(overallAvg))} />
          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>{reviews.length} total reviews</div>
        </div>
        {ratingBreakdown.map((rb) => (
          <div key={rb.source} style={{
            flex: '1 1 140px', padding: '16px', backgroundColor: '#fff', borderRadius: '12px',
            border: '1px solid #e2e8f0', textAlign: 'center',
          }}>
            <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '600', marginBottom: '6px', textTransform: 'uppercase' }}>
              {rb.source}
            </div>
            <div style={{ fontSize: '22px', fontWeight: '700', color: '#1e293b' }}>{rb.avgRating}</div>
            <div style={{ fontSize: '11px', color: '#94a3b8' }}>{rb.count} reviews</div>
          </div>
        ))}
      </div>

      {/* Rating trend chart */}
      <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px', marginBottom: '24px' }}>
        <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
          Rating Trend (Monthly Averages)
        </h3>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={ratingTrend}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94a3b8' }} />
            <YAxis domain={[1, 5]} tick={{ fontSize: 11, fill: '#94a3b8' }} />
            <Tooltip contentStyle={{ fontSize: '13px', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
            <Line type="monotone" dataKey="avgRating" stroke="#f59e0b" strokeWidth={2} dot={{ r: 4, fill: '#f59e0b' }} name="Average Rating" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Reviews list */}
      <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '24px' }}>
        <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#1e293b', marginBottom: '20px' }}>
          All Reviews
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {reviews.map((review) => (
            <div key={review.id} style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: '20px' }}>
              {/* Review header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b' }}>{review.author}</span>
                <StatusBadge status={review.source === 'SM-Compare' ? 'active' : 'pending'} customLabel={review.source} />
                <StarRating rating={review.rating} />
                <span style={{ fontSize: '12px', color: '#94a3b8', marginLeft: 'auto' }}>
                  {new Date(review.date).toLocaleDateString('en-CA')}
                </span>
              </div>

              {/* Review text */}
              <p style={{ fontSize: '14px', color: '#475569', lineHeight: '1.6', margin: '0 0 10px' }}>
                {review.text}
              </p>

              {/* Existing reply */}
              {review.reply && (
                <div style={{
                  marginLeft: '20px', padding: '10px 14px', backgroundColor: '#f8fafc',
                  borderLeft: '3px solid #3b82f6', borderRadius: '0 8px 8px 0', fontSize: '13px',
                  color: '#475569', marginBottom: '10px',
                }}>
                  <div style={{ fontWeight: '600', color: '#1e293b', marginBottom: '4px', fontSize: '12px' }}>Your Reply:</div>
                  {review.reply}
                </div>
              )}

              {/* Actions */}
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {!review.reply && replyingTo !== review.id && (
                  <button
                    type="button"
                    onClick={() => { setReplyingTo(review.id); setReplyText(''); }}
                    style={{
                      padding: '4px 12px', fontSize: '12px', fontWeight: '600', borderRadius: '6px',
                      border: '1px solid #d1d5db', backgroundColor: '#fff', color: '#374151', cursor: 'pointer',
                    }}
                  >
                    Reply
                  </button>
                )}
                {!review.reported && (
                  <button
                    type="button"
                    onClick={() => reportMutation.mutate(review.id)}
                    style={{
                      padding: '4px 12px', fontSize: '12px', fontWeight: '600', borderRadius: '6px',
                      border: '1px solid #fecaca', backgroundColor: '#fff', color: '#dc2626', cursor: 'pointer',
                    }}
                  >
                    Report
                  </button>
                )}
                {review.reported && (
                  <span style={{ fontSize: '12px', color: '#dc2626', fontWeight: '500' }}>Reported</span>
                )}
              </div>

              {/* Reply form */}
              {replyingTo === review.id && (
                <div style={{ marginTop: '12px', marginLeft: '20px' }}>
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    rows={3}
                    placeholder="Write your reply..."
                    style={{
                      width: '100%', padding: '10px 12px', fontSize: '13px', borderRadius: '8px',
                      border: '1px solid #d1d5db', resize: 'vertical', fontFamily: 'inherit',
                      boxSizing: 'border-box',
                    }}
                  />
                  <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                    <button
                      type="button"
                      onClick={() => handleReplySubmit(review.id)}
                      disabled={replyMutation.isPending || !replyText.trim()}
                      style={{
                        padding: '6px 16px', fontSize: '13px', fontWeight: '600', borderRadius: '6px',
                        border: 'none', backgroundColor: '#3b82f6', color: '#fff', cursor: 'pointer',
                        opacity: !replyText.trim() ? 0.5 : 1,
                      }}
                    >
                      {replyMutation.isPending ? 'Sending...' : 'Submit Reply'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setReplyingTo(null)}
                      style={{
                        padding: '6px 16px', fontSize: '13px', fontWeight: '600', borderRadius: '6px',
                        border: '1px solid #d1d5db', backgroundColor: '#fff', color: '#475569', cursor: 'pointer',
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
